'use client';

import React from 'react';
import { Sparkles, X, RefreshCw, MessageSquare, Users } from 'lucide-react';

interface InspectBoardModalProps {
  inspectBoardId: string | null;
  inspectBoardData: { board: any; totalCards: number; lists: any[] } | null;
  loadingCards: boolean;
  onClose: () => void;
  handleOpenCardDetail: (cardId: string) => void;
}

export const InspectBoardModal: React.FC<InspectBoardModalProps> = ({
  inspectBoardId,
  inspectBoardData,
  loadingCards,
  onClose,
  handleOpenCardDetail,
}) => {
  if (!inspectBoardId) return null;

  return (
    <div className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0D1122] border border-slate-800 rounded-3xl max-w-4xl w-full p-6 space-y-6 shadow-2xl relative max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-purple-500/15 border border-purple-500/30 text-purple-400 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-white flex items-center gap-2">
                <span>{inspectBoardData?.board?.title || 'Board'} Cards</span>
                {inspectBoardData && (
                  <span className="px-2.5 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-full text-xs font-mono font-bold">
                    Total {inspectBoardData.totalCards} Cards
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-400 font-mono">Workspace: {inspectBoardData?.board?.workspace?.name}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pr-1 space-y-6">
          {loadingCards ? (
            <div className="py-16 text-center text-slate-400 space-y-3">
              <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto" />
              <p className="text-xs font-medium">Fetching board cards and lists...</p>
            </div>
          ) : !inspectBoardData?.lists || inspectBoardData.lists.length === 0 ? (
            <div className="py-16 text-center text-slate-500 border border-dashed border-slate-800 rounded-2xl">
              <Sparkles className="w-10 h-10 text-slate-600 mx-auto mb-2 opacity-50" />
              <p className="text-xs font-bold text-slate-400">No Columns or Cards Found</p>
              <p className="text-[11px] text-slate-500">This board currently has no list columns or task cards.</p>
            </div>
          ) : (
            inspectBoardData.lists.map((list) => (
              <div key={list.id} className="space-y-3 bg-[#060811] border border-slate-800/80 rounded-2xl p-4">
                <div className="flex items-center justify-between border-b border-slate-800/80 pb-2">
                  <h4 className="text-xs font-extrabold text-indigo-300 uppercase tracking-wider flex items-center gap-2">
                    <span>Column: {list.title}</span>
                    <span className="px-2 py-0.5 bg-slate-800 text-slate-300 text-[10px] rounded-full font-mono">
                      {list.cardCount} cards
                    </span>
                  </h4>
                </div>

                {list.cards.length === 0 ? (
                  <p className="text-xs text-slate-600 italic py-2">No cards in this column.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {list.cards.map((card: any) => (
                      <div
                        key={card.id}
                        onClick={() => handleOpenCardDetail(card.id)}
                        className="bg-[#0A0D1A] border border-slate-800/80 hover:border-indigo-500/50 rounded-xl p-3.5 space-y-2 transition-all group flex flex-col justify-between cursor-pointer"
                      >
                        <div>
                          <h5 className="text-xs font-bold text-white group-hover:text-indigo-300 transition-colors">
                            {card.title}
                          </h5>
                          {card.description && (
                            <p className="text-[11px] text-slate-400 line-clamp-2 mt-1">{card.description}</p>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-slate-800/60 text-[10px] text-slate-400">
                          <div className="flex items-center gap-2">
                            {card.commentsCount > 0 && (
                              <span className="flex items-center gap-1 text-slate-400 font-mono">
                                <MessageSquare className="w-3 h-3 text-indigo-400" /> {card.commentsCount}
                              </span>
                            )}
                            {card.assignees?.length > 0 && (
                              <span className="flex items-center gap-1 text-slate-400 font-mono">
                                <Users className="w-3 h-3 text-purple-400" /> {card.assignees.length} assigned
                              </span>
                            )}
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenCardDetail(card.id);
                            }}
                            className="px-2.5 py-1 bg-indigo-600/20 hover:bg-indigo-600 text-indigo-300 hover:text-white rounded-lg border border-indigo-500/30 text-[10px] font-extrabold transition-all cursor-pointer"
                          >
                            View Card &rarr;
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
